import { useState, useRef, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { LogOut, Loader2, Cable, Clock, Moon, Sun } from 'lucide-react';
import { useSession, markLoggedOut } from '@/lib/session';
import { useTheme, toggleTheme } from '@/store/theme';
import { LoginButton } from './LoginButton';
import { logout } from '@/lib/oauth';
import { cn } from '@/lib/cn';

/**
 * Top-bar user widget — also serves sebagai utility menu untuk semua
 * secondary action (MCP setup, History, Theme toggle, Sign out).
 *
 * Three states:
 *   - loading        → spinner pill
 *   - unauthenticated → LoginButton ghost variant. Tidak ada utility menu
 *                       di state ini — semua action (MCP, History) butuh
 *                       login dulu.
 *   - authenticated  → avatar pill yang buka dropdown panel:
 *                        - user info (email + role)
 *                        - nav: MCP setup, History
 *                        - settings: Theme toggle
 *                        - sign out
 *
 * Tujuan refactor: TopBar dulunya punya 5+ pill buttons (cluttered).
 * Sekarang collapse ke 1 dropdown. Pattern: GitHub/Linear/Notion.
 */
export function UserMenu() {
  const session = useSession();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  if (session.loading) {
    return (
      <div className="w-10 h-10 rounded-pill border-2 border-border-strong bg-elevated flex items-center justify-center shadow-brutal-sm">
        <Loader2 size={14} className="animate-spin text-fg-tertiary" />
      </div>
    );
  }

  if (!session.authenticated) {
    return <LoginButton variant="ghost" />;
  }

  const initial = (session.user?.email || session.user?.uid || '?')[0]?.toUpperCase() ?? '?';

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-10 h-10 rounded-pill border-2 border-border-strong bg-accent',
          'flex items-center justify-center font-display font-extrabold text-base transition-all',
          'shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5',
          'active:translate-y-0.5 active:shadow-brutal-none',
          open && 'shadow-brutal-none translate-y-0.5'
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
      >
        {initial}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal overflow-hidden z-50">
          {/* User info header */}
          <div className="px-4 py-3 border-b-2 border-border-strong bg-method-get/40">
            <div className="text-2xs font-display font-bold uppercase tracking-wider text-fg-secondary mb-1">
              Signed in as
            </div>
            <div className="text-sm font-bold truncate">{session.user?.email}</div>
            {session.user?.role && (
              <div className="text-2xs text-fg-tertiary mt-0.5 capitalize font-mono">
                {session.user.role}
              </div>
            )}
          </div>

          {/* Nav section — secondary pages */}
          <div className="border-b-2 border-border-strong">
            <MenuLink to="/mcp" icon={<Cable size={14} />} label="MCP setup" onNav={() => setOpen(false)} />
            <MenuLink to="/history" icon={<Clock size={14} />} label="Request history" onNav={() => setOpen(false)} />
          </div>

          {/* Settings */}
          <div className="border-b-2 border-border-strong">
            <button
              type="button"
              onClick={toggleTheme}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-display font-bold text-fg hover:bg-overlay transition-colors text-left"
            >
              <span className="flex items-center gap-2">
                {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
                Theme
              </span>
              <span className="text-2xs font-mono text-fg-tertiary capitalize">{theme}</span>
            </button>
          </div>

          {/* Sign out — destructive accent on hover */}
          <button
            type="button"
            onClick={async () => {
              markLoggedOut();
              await logout();
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm font-display font-bold text-fg hover:bg-method-delete/40 transition-colors text-left"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Internal helper untuk nav link item. Close dropdown on click via onNav.
 */
function MenuLink({
  to,
  icon,
  label,
  onNav,
}: {
  to: '/mcp' | '/history';
  icon: React.ReactNode;
  label: string;
  onNav: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNav}
      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-display font-bold text-fg hover:bg-overlay transition-colors"
    >
      {icon}
      {label}
    </Link>
  );
}
